/**
 * COUNTRY — the profile's country picker (owner's player-card assignment 7.10.2026, §5: the card's country is the
 * player's own explicit choice, never the room's or the connection's). Laid out by Codex on the LANGUAGE panel's pattern
 * (art/src/player-card-2026-10-07/profile/layout-profile-add.json, `countryPicker`): the same drawn frame with its own ×,
 * one column of the kit's row plates so long names fit on two lines, only the list scrolls, the game's own rail beside it.
 * Every name is live text from the shared country list; no flag — that is the owner's open question.
 */
import { useRef } from 'preact/hooks';
import { COUNTRIES_BY_NAME } from '@snw/protocol';
import { art } from '../art';
import { ScrollRail } from './ScrollRail';
import { sfx } from '../audio/sfx';
import PA from '../layouts/player-card-2026-10-07/profile/layout-profile-add.json';

type Box = { x: number; y: number; w: number; h: number };
const CP = (PA as unknown as { countryPicker: { frame: Box; title: Box & { size: number }; close: Box; list: Box & { rowH: number; rowPitch: number; label: { x: number; w: number } }; rail: Box } }).countryPicker;
const F = CP.frame;

export function CountryPanel({ value, onPick, onClose }: { value: string | null; onPick: (code: string) => void; onClose: () => void }) {
  const listRef = useRef<HTMLDivElement>(null);
  const close = () => { sfx.tap(); onClose(); };
  const L = CP.list;
  return (
    <div class="sheet-backdrop howto-backdrop" data-testid="country-panel" onClick={close}>
      <div class="cp-panel" style={{ left: F.x, top: F.y, width: F.w, height: F.h, '--panel': `url(${art('/art/lang-cut/panel.png')})` }} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Country">
        <div class="cp-title" style={{ left: CP.title.x, top: CP.title.y, width: CP.title.w, height: CP.title.h }}>Country</div>
        <button class="cp-close" type="button" data-testid="country-close" aria-label="Close country selection" style={{ left: CP.close.x, top: CP.close.y, width: CP.close.w, height: CP.close.h }} onClick={close} />
        <div class="cp-list-wrap" style={{ left: L.x, top: L.y, width: CP.rail.x + CP.rail.w - L.x, height: L.h }}>
          <div class="cp-list" ref={listRef} data-testid="country-list" style={{ width: L.w, height: L.h, '--row-off': `url(${art('/art/lang-cut/row.es.png')})`, '--row-on': `url(${art('/art/lang-cut/row.en.png')})` }}>
            <div class="cp-list-inner" style={{ height: COUNTRIES_BY_NAME.length * L.rowPitch - (L.rowPitch - L.rowH) }}>
              {COUNTRIES_BY_NAME.map((c, i) => { const on = c.code === value; return (
                <button key={c.code} class={`cp-row${on ? ' on' : ''}`} type="button" data-testid={`country-${c.code}`} aria-pressed={on}
                  style={{ top: i * L.rowPitch, width: L.w, height: L.rowH }} onClick={() => { sfx.select(); onPick(c.code); }}>
                  <span class="cp-name" style={{ left: L.label.x, width: L.label.w }}>{c.name}</span>
                </button>); })}
            </div>
          </div>
          <ScrollRail target={listRef} testid="country-scrollbar" />
        </div>
      </div>
    </div>
  );
}
